import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { OriginationInput, OriginationView } from "@shared/originationConstants";

/** The one origination row on a company, and the two ways to change it. */
export function useOrigination(companyId: string | undefined) {
  const qc = useQueryClient();
  const query = useQuery<OriginationView | null>({
    queryKey: ["origination", companyId],
    queryFn: async () => {
      const r = await fetch(`/api/companies/${companyId}/origination`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load source");
      return r.json();
    },
    enabled: !!companyId,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["origination", companyId] });
    qc.invalidateQueries({ queryKey: ["company", companyId] });
    qc.invalidateQueries({ queryKey: ["/api/prospects"] });
    qc.invalidateQueries({ queryKey: ["cois"] });
    qc.invalidateQueries({ queryKey: ["coi"] });
    qc.invalidateQueries({ queryKey: ["coi-bd"] });
    qc.invalidateQueries({ queryKey: ["suggestions"] });
  };
  const save = useMutation({
    mutationFn: async (input: OriginationInput) => {
      const r = await fetch(`/api/companies/${companyId}/origination`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Save failed");
      return r.json() as Promise<OriginationView>;
    },
    onSuccess: invalidate,
  });
  const clear = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/companies/${companyId}/origination`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Clear failed");
    },
    onSuccess: invalidate,
  });
  return { ...query, save, clear };
}
